<template>
  <div>
    <Grid
      @api="getKeys()"
      @rendered="renderComplete = true"
      :headers="headers"
      :payload="{results, count, next, previous}"
      @filter="showFilterModal = true"
      @clearFilters="$refs.desktopFilters.reset()"
    >
      <template #buttons>
        <base-button
          v-if="$store.getters['auth/isStaff']"
          @click="showCommission = true"
          size="sm"
          context="outline"
          action="register_lora"
        >
          <base-icon icon="register" class="text-base" />
          <template v-if="withDeviceEui.length > 0" #after>
            <span class="opacity-75 font-medium ml-2">({{ withDeviceEui.length }})</span>
          </template>
        </base-button>

        <div v-tooltip="checked.length ? '' : {content: transferKeysInactiveBtnTooltipText}">
          <base-button
            :disabled="checked.length < 1"
            @click="showTransfer = true"
            size="sm"
            action="transfer"
          >
            <base-icon icon="transfer" class="text-base" />
            <template #after>
              <span class="opacity-75 font-medium ml-2">({{ checked.length }})</span>
            </template>
          </base-button>
        </div>

        <base-dropdown-2
          v-if="count > 0"
          :loading="isDownloading"
          :disabled="isDownloading"
          size="sm"
        >
          <template>
            <base-icon icon="download" class="text-xl mr-2" />
            {{ $t(`mygwf.components.Button.key_export`) }}
            <span class="opacity-75 font-medium ml-2">({{ count }})</span></template
          >
          <template #body>
            <base-button
              @click="exportKeys('json')"
              size="sm"
              context="outline"
              class="w-full border-b"
              action="json"
            />
            <base-button
              @click="exportKeys('csv')"
              size="sm"
              context="outline"
              class="w-full border-b"
              action="csv"
            />
            <base-button
              @click="exportKeys('xml')"
              size="sm"
              context="outline"
              class="w-full border-b"
              action="xml"
            />
          </template>
        </base-dropdown-2>
      </template>

      <template #filter>
        <KeyFilter ref="desktopFilters" />
      </template>

      <template #[`head.check`]>
        <div v-if="showSelectable && renderComplete" class="bg-secondary text-left text-base">
          <base-check v-model="checkAll" />
        </div>
      </template>

      <template #[`head.actions`]>
        <div class="bg-secondary"></div>
      </template>

      <template #[`body.check`]="{item}">
        <div v-if="showSelectable" class="text-base">
          <base-check v-model="checked" :value="item[pk]" />
        </div>
      </template>

      <template #[`body.serial_number`]="{item}">
        <span class="flex flex-col space-y-1">
          <span :class="['flex flex-col leading-5 font-medium']">
            <span>{{ getSerial(item) }}</span>
            <div v-if="item.platform" class="flex-shrink-0">
              <span
                class="px-2 inline-flex text-xs leading-5 font-semibold rounded text-white bg-blue-light"
                >{{ item.platform }}</span
              >
            </div>
          </span>
          <span class="leading-5 text-secondary-dark">
            {{ item.comm_mod_type_name }}
          </span>
        </span>
      </template>
      <template #[`body.order__delivery_order_number`]="{item}">
        <span v-if="renderComplete">
          {{ item.delivery_order_number }}
        </span>
      </template>
      <template #[`body.company`]="{item}">
        <div :class="['flex flex-col lg:flex-row space-x-6 leading-5 font-medium']">
          <div class="flex-shrink-0 lg:ml-2">
            <span
              class="px-2 inline-flex text-xs leading-5 font-semibold rounded text-gray-600 bg-secondary"
              >{{ item.company_area_code }}-{{ item.company_account }}</span
            >
          </div>
          <div class="flex flex-col lg:flex-row lg:items-center leading-5 truncate">
            <span class="truncate">{{ item.company }}</span>
          </div>
        </div>
      </template>
      <template #[`body.created_at`]="{item}">
        <span v-if="renderComplete">
          {{ item.created_at ? $d(new Date(item.created_at), 'long') : '-' }}
        </span>
      </template>
      <template #[`body.actions`]="{item}">
        <base-button-icon
          @click="toggleKeyDetails(item[pk])"
          tooltip="Key"
          class="bg-secondary-light"
          :icon="visibleKeys.includes(item[pk]) ? `expand-up` : `expand-down`"
        />
      </template>

      <template #alert="{fetching}">
        <base-alert
          v-if="!fetching && showLoadAll"
          :link="$store.getters['app/queryAll']"
          type="info"
          :text="{
            msg: 'get_all_results',
            part: `${results.length} ${$store.getters['app/pageTitle']}`,
          }"
          size="sm"
        />
      </template>

      <template #expanded="{item}">
        <transition name="slidedown" class="slidedown transition-all">
          <div v-if="visibleKeys.includes(item[pk])">
            <div class="grid-expanded">
              <div class="flex flex-wrap items-center content-around text-sm text-gray-700">
                <div
                  class="flex flex-col"
                  v-for="(key, index) in Object.keys(item.data)"
                  :key="`${index}-${item[pk]}`"
                >
                  <div class="shadow bg-white p-3 m-3">
                    <div class="flex flex-col justify-between">
                      <span class="capitalize mb-1">{{ text(key) }}:</span
                      ><span class="font-semibold">{{ item.data[key] }}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </transition>
      </template>
    </Grid>

    <!-- Mobile filters -->
    <transition v-if="showFilterModal" name="grow">
      <base-modal @close="showFilterModal = false" head="filters" bg="white" full-width>
        <KeyFilter :mobile="true" ref="mobileFilter" />
        <!-- Action buttons -->
        <div class="pt-4 mt-6 border-t flex flex-col justify-end w-full">
          <div class="flex flex-col">
            <div class="flex space-x-4">
              <base-button
                class="w-1/2"
                @click="
                  $refs.mobileFilter.reset()
                  showFilterModal = false
                "
                action="clear"
                context="secondary"
              >
                <base-icon icon="close" />
              </base-button>
              <base-button
                class="w-1/2"
                @click="
                  $refs.mobileFilter.submit()
                  showFilterModal = false
                "
                action="submit"
              >
                <base-icon icon="filters" />
              </base-button>
            </div>
          </div>
        </div>
      </base-modal>
    </transition>
    <KeyTransfer
      v-if="showTransfer"
      :items="selectedKeys"
      @close="showTransfer = false"
      @success="getKeys()"
    />
    <KeyCommission
      v-if="showCommission"
      :items="withDeviceEui"
      @close="showCommission = false"
      @success="getKeys()"
    />
  </div>
</template>

<script>
import isEmpty from 'lodash-es/isEmpty'
import fileExport from '@/state/helpers/fileExport.js'
import {COMMODULE_DELETE, TRANSFER_CREATE} from '@/state/helpers/permissions.js'
import orderBy from 'lodash-es/orderBy'
import {
  BaseAlert,
  BaseButtonIcon,
  BaseButton,
  BaseCheck,
  BaseDropdown,
  BaseDropdown2,
  BaseIcon,
  BaseModal,
} from '@/components/base'
import Grid from '@/components/shared/Grid.vue'

export default {
  data() {
    return {
      checked: [],
      list: {},
      visibleKeys: [],
      showTransfer: false,
      showCommission: false,
      renderComplete: false,
      pk: this.$store.getters['keys/pk'],
      currentDeleting: 0,
      currentSending: 0,
      isDownloading: false,
      headers: {
        check: {
          label: '',
          order: true,
          className: 'w-16',
        },
        serial_number: {
          label: 'serial_number',
          order: true,
        },
        order__delivery_order_number: {
          label: 'delivery_order_number',
          order: true,
        },
        company: {
          label: 'company',
          order: true,
          className: 'w-1/4',
        },
        created_at: {
          label: 'created',
          order: true,
        },
        actions: {
          label: '',
          order: false,
          className: 'lg:w-20',
        },
      },
      showFilterModal: false,
    }
  },
  methods: {
    async getKeys() {
      try {
        this.list = await this.$store.dispatch('keys/get')
        if (!this.keysSorted) {
          this.list.results = orderBy(
            this.list.results,
            [key => new Date(key.created_at)],
            ['desc']
          )
        }
        this.checked = []
      } catch (error) {}
    },
    toggleKeyDetails(id) {
      if (!this.visibleKeys.includes(id)) {
        return this.visibleKeys.push(id)
      }
      return this.visibleKeys.splice(this.visibleKeys.indexOf(id), 1)
    },
    text(field) {
      return field.split('_').join(' ')
    },
    getSerial({data}) {
      if (data) {
        return data.serial || data.device_eui || data.serial_number
      }
      return `-`
    },
    exportKeys(type) {
      this.isDownloading = true

      this.$store
        .dispatch(`keys/export`, type.toLowerCase())
        .then(file => {
          const d = new Date()
          const filename = `${this.$t(`mygwf.views.Keys.title`)}-${d
            .toISOString()
            .substring(0, 10)}.${type.toLowerCase()}`
          fileExport(file, filename)
        })
        .then(() => {
          this.$store.dispatch('app/notification', {
            type: 'success',
            group: 'app',
            text: `download`,
          })
        })
        .catch(err => console.log(err))
        .finally(() => (this.isDownloading = false))
    },
  },
  computed: {
    count() {
      return this.list && this.list.count ? this.list.count : 0
    },
    next() {
      return this.list && this.list.next ? this.list.next : null
    },
    previous() {
      return this.list && this.list.previous ? this.list.previous : null
    },
    results() {
      return this.list && this.list.results ? Object.freeze(this.list.results) : []
    },
    selectedKeys() {
      return this.results.filter(key => this.checked.indexOf(key[this.pk]) > -1)
    },
    withDeviceEui() {
      return this.selectedKeys.filter(item => item.data.device_eui)
    },
    showSelectable() {
      return (
        this.$store.getters['auth/isPermitted'](COMMODULE_DELETE) ||
        this.$store.getters['auth/isPermitted'](TRANSFER_CREATE)
      )
    },
    transferKeysInactiveBtnTooltipText() {
      return this.$t('mygwf.components.Keys.select_records_to_transfer')
    },
    checkAll: {
      get: function () {
        return (
          this.results.length > 0 &&
          this.checked.length === this.results.length &&
          this.checked[0] === this.results[0][this.pk]
        )
      },
      set: function (value) {
        const keys = this.results.reduce((acc, key) => [...acc, key[this.pk]], [])
        this.checked = value ? [...keys] : []
      },
    },
    showLoadAll() {
      const {limit, offset, ordering, ...params} = this.$route.query
      return !isEmpty(params) && this.results.length < this.count
    },
    keysSorted() {
      return this.$route.query.ordering
    },
  },
  components: {
    KeyTransfer: () => import('./KeyTransfer.vue'),
    KeyCommission: () => import('./KeyCommission.vue'),
    KeyFilter: () => import('./KeyFilter.vue'),
    BaseAlert,
    BaseButton,
    BaseButtonIcon,
    BaseCheck,
    BaseDropdown,
    BaseDropdown2,
    BaseIcon,
    BaseModal,
    Grid,
  },
}
</script>
